import React, { useState } from 'react';
import { Users, Calendar, CheckSquare, FileText, Clock, BarChart2, ChevronLeft, ChevronRight, Archive, AlertTriangle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { CancelCommitteeModal } from './CancelCommitteeModal';
export function CommitteeDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const committeeId = parseInt(id || '1');
  const [activeTab, setActiveTab] = useState('overview');
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Determine if this is a parent committee with sub-committees
  const isParentCommittee = committeeId < 100;
  // Mock committee data
  const committee = {
    id: committeeId,
    name: committeeId === 1 ? 'Governance Committee' : 'Finance Committee',
    description: committeeId === 1 ? 'Oversees corporate governance matters, board effectiveness, and ensures compliance with regulatory requirements.' : 'Responsible for overseeing financial reporting, budgeting, and audit processes within the organization.',
    type: isParentCommittee ? 'parent' : 'standard',
    parentId: isParentCommittee ? null : Math.floor(committeeId / 100),
    members: [{
      id: 1,
      name: 'Mohammed Al Hashimi',
      role: 'Chairperson',
      department: 'Finance',
      avatar: 'M'
    }, {
      id: 2,
      name: 'Aisha Al Mansoori',
      role: 'Secretary',
      department: 'Finance',
      avatar: 'A'
    }, {
      id: 3,
      name: 'Omar Al Shamsi',
      role: 'Member',
      department: 'Operations',
      avatar: 'O'
    }, {
      id: 4,
      name: 'Fatima Al Zaabi',
      role: 'Member',
      department: 'Accounting',
      avatar: 'F'
    }, {
      id: 5,
      name: 'Ahmed Al Dhaheri',
      role: 'Member',
      department: 'Internal Audit',
      avatar: 'A'
    }],
    subCommittees: isParentCommittee ? [{
      id: committeeId * 100 + 1,
      name: committeeId === 1 ? 'Board Governance Team' : 'Budget Planning Team',
      members: 5,
      lastMeeting: '2023-08-15',
      status: 'active',
      completion: 75
    }, {
      id: committeeId * 100 + 2,
      name: committeeId === 1 ? 'Regulatory Compliance Team' : 'Financial Reporting Team',
      members: 4,
      lastMeeting: '2023-08-12',
      status: 'active',
      completion: 60
    }, {
      id: committeeId * 100 + 3,
      name: committeeId === 1 ? 'Shareholder Relations Team' : 'Investment Review Team',
      members: 6,
      lastMeeting: '2023-08-08',
      status: 'active',
      completion: 80
    }] : [],
    tasks: [{
      id: 1,
      title: committeeId === 1 ? 'Review governance policies' : 'Review Q3 financial reports',
      status: 'completed',
      dueDate: '2023-09-15'
    }, {
      id: 2,
      title: committeeId === 1 ? 'Update board charter' : 'Prepare budget proposal for 2024',
      status: 'pending',
      dueDate: '2023-10-10'
    }, {
      id: 3,
      title: committeeId === 1 ? 'Conduct board evaluation' : 'Audit expense reports from Q2',
      status: 'pending',
      dueDate: '2023-09-30'
    }],
    meetings: [{
      id: 1,
      title: committeeId === 1 ? 'Quarterly Governance Review' : 'Monthly Financial Review',
      date: '2023-09-15',
      time: '10:00 AM',
      location: 'Online'
    }, {
      id: 2,
      title: committeeId === 1 ? 'Board Charter Discussion' : 'Budget Planning Session',
      date: '2023-09-28',
      time: '02:00 PM',
      location: 'Conference Room A'
    }],
    documents: [{
      id: 1,
      name: committeeId === 1 ? 'Governance Committee Charter.pdf' : 'Finance Committee Charter.pdf',
      type: 'PDF',
      uploadedDate: '2023-08-15'
    }, {
      id: 2,
      name: committeeId === 1 ? 'Board Evaluation Template.xlsx' : 'Q2 Financial Report.xlsx',
      type: 'XLSX',
      uploadedDate: '2023-07-20'
    }, {
      id: 3,
      name: committeeId === 1 ? 'Governance Best Practices.docx' : 'Budget Guidelines 2024.docx',
      type: 'DOCX',
      uploadedDate: '2023-08-30'
    }],
    stats: {
      completionRate: 75,
      meetingsHeld: 12,
      documentsUploaded: 25,
      tasksCompleted: 45
    },
    statusHistory: [{
      id: 1,
      status: 'Created',
      date: '2023-01-15',
      by: 'Ahmed Al Dhaheri',
      note: 'Committee established by Board Resolution #2023-05'
    }, {
      id: 2,
      status: 'Active',
      date: '2023-01-20',
      by: 'Mohammed Al Hashimi',
      note: 'First meeting held and committee officially activated'
    }]
  };
  const handleCancelCommittee = (reason: string) => {
    console.log(`Committee ${committee.id} cancelled with reason: ${reason}`);
    // In a real app, this would send the cancellation to the backend
    setShowCancelModal(false);
    // Redirect to committees page or show success message
    // window.location.href = '/committees';
  };
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'delayed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div data-id="element-366">
      <div className="flex items-center mb-6" data-id="element-367">
        <Link to="/committees" className="flex items-center text-gray-600 hover:text-gray-900 mr-4" data-id="element-368">
          <ChevronLeft size={20} className="mr-1" data-id="element-369" />
          Back
        </Link>
        <h1 className="text-2xl font-bold" data-id="element-370">{committee.name}</h1>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-6" data-id="element-371">
        <div className="flex border-b border-gray-200" data-id="element-372">
          <button className={`px-4 py-3 font-medium ${activeTab === 'overview' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('overview')} data-id="element-373">
            Overview
          </button>
          {isParentCommittee && <button className={`px-4 py-3 font-medium ${activeTab === 'subcommittees' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('subcommittees')} data-id="element-374">
              Sub-Committees
            </button>}
          <button className={`px-4 py-3 font-medium ${activeTab === 'members' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('members')} data-id="element-375">
            Members
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'tasks' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('tasks')} data-id="element-376">
            Tasks
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'meetings' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('meetings')} data-id="element-377">
            Meetings
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'documents' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('documents')} data-id="element-378">
            Documents
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'history' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('history')} data-id="element-379">
            Status History
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-380">
          {/* Committee Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 lg:col-span-2" data-id="element-381">
            <h2 className="text-lg font-semibold mb-4" data-id="element-382">
              Committee Information
            </h2>
            <p className="text-gray-700 mb-4" data-id="element-383">{committee.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-id="element-384">
              <div className="bg-blue-50 p-4 rounded-lg" data-id="element-385">
                <div className="flex items-center text-blue-500 mb-2" data-id="element-386">
                  <Users size={18} className="mr-2" data-id="element-387" />
                  <span className="font-medium" data-id="element-388">Members</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-389">{committee.members.length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg" data-id="element-390">
                <div className="flex items-center text-green-500 mb-2" data-id="element-391">
                  <CheckSquare size={18} className="mr-2" data-id="element-392" />
                  <span className="font-medium" data-id="element-393">Tasks</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-394">{committee.tasks.length}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg" data-id="element-395">
                <div className="flex items-center text-purple-500 mb-2" data-id="element-396">
                  <Calendar size={18} className="mr-2" data-id="element-397" />
                  <span className="font-medium" data-id="element-398">Meetings</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-399">
                  {committee.meetings.length}
                </p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg" data-id="element-400">
                <div className="flex items-center text-orange-500 mb-2" data-id="element-401">
                  <FileText size={18} className="mr-2" data-id="element-402" />
                  <span className="font-medium" data-id="element-403">Documents</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-404">
                  {committee.documents.length}
                </p>
              </div>
            </div>
            <div className="mt-6" data-id="element-405">
              <h3 className="font-medium mb-2" data-id="element-406">Committee Progress</h3>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-407">
                <div className="h-2.5 rounded-full bg-orange-500" style={{
              width: `${committee.stats.completionRate}%`
            }} data-id="element-408"></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500" data-id="element-409">
                <span data-id="element-410">Overall Completion</span>
                <span data-id="element-411">{committee.stats.completionRate}%</span>
              </div>
            </div>
          </div>
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-412">
            <h2 className="text-lg font-semibold mb-4" data-id="element-413">Quick Actions</h2>
            <div className="space-y-3" data-id="element-414">
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-415">
                <Calendar size={18} className="mr-2" data-id="element-416" />
                Schedule Meeting
              </button>
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-417">
                <CheckSquare size={18} className="mr-2" data-id="element-418" />
                Add Task
              </button>
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-419">
                <FileText size={18} className="mr-2" data-id="element-420" />
                Upload Document
              </button>
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-421">
                <Users size={18} className="mr-2" data-id="element-422" />
                Manage Members
              </button>
              {isParentCommittee && <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-423">
                  <Users size={18} className="mr-2" data-id="element-424" />
                  Add Sub-Committee
                </button>}
              <button className="w-full flex items-center justify-center p-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors" onClick={() => setShowCancelModal(true)} data-id="element-425">
                <Archive size={18} className="mr-2" data-id="element-426" />
                Cancel Committee
              </button>
            </div>
          </div>
        </div>}

      {/* Sub-Committees Tab */}
      {activeTab === 'subcommittees' && isParentCommittee && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-427">
          <div className="flex justify-between items-center mb-4" data-id="element-428">
            <h2 className="text-lg font-semibold" data-id="element-429">Sub-Committees</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-430">
              Add Sub-Committee
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-431">
            <table className="w-full" data-id="element-432">
              <thead data-id="element-433">
                <tr className="bg-gray-50" data-id="element-434">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-435">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-436">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-437">
                    Last Meeting
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-438">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-439">
                    Completion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-440">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-441">
                {committee.subCommittees.map(subCommittee => <tr key={subCommittee.id} data-id="element-442">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-443">
                      <div className="font-medium text-gray-900" data-id="element-444">
                        {subCommittee.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-445">
                      <div className="flex items-center" data-id="element-446">
                        <Users size={16} className="text-gray-500 mr-2" data-id="element-447" />
                        <span data-id="element-448">{subCommittee.members}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-449">
                      <div className="flex items-center" data-id="element-450">
                        <Calendar size={16} className="text-gray-500 mr-2" data-id="element-451" />
                        <span data-id="element-452">{subCommittee.lastMeeting}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-453">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${subCommittee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-454">
                        {subCommittee.status === 'active' ? 'Active' : 'Completed'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-455">
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-456">
                        <div className={`h-2.5 rounded-full ${subCommittee.completion >= 80 ? 'bg-green-500' : subCommittee.completion >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{
                    width: `${subCommittee.completion}%`
                  }} data-id="element-457"></div>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-458">
                        {subCommittee.completion}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-459">
                      <Link to={`/committees/${subCommittee.id}`} className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-460">
                        View
                      </Link>
                      <Link to={`/committees/${subCommittee.id}/edit`} className="text-orange-500 hover:text-orange-700" data-id="element-461">
                        Edit
                      </Link>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Members Tab */}
      {activeTab === 'members' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-462">
          <div className="flex justify-between items-center mb-4" data-id="element-463">
            <h2 className="text-lg font-semibold" data-id="element-464">Committee Members</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-465">
              Add Member
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-466">
            <table className="w-full" data-id="element-467">
              <thead data-id="element-468">
                <tr className="bg-gray-50" data-id="element-469">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-470">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-471">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-472">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-473">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-474">
                {committee.members.map(member => <tr key={member.id} data-id="element-475">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-476">
                      <div className="flex items-center" data-id="element-477">
                        <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3" data-id="element-478">
                          {member.avatar}
                        </div>
                        <span className="font-medium" data-id="element-479">{member.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-480">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${member.role === 'Chairperson' ? 'bg-blue-100 text-blue-800' : member.role === 'Secretary' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-481">
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-482">
                      {member.department}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-483">
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-484">
                        View
                      </button>
                      <button className="text-orange-500 hover:text-orange-700" data-id="element-485">
                        Message
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-486">
          <div className="flex justify-between items-center mb-4" data-id="element-487">
            <h2 className="text-lg font-semibold" data-id="element-488">Tasks</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-489">
              Add Task
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-490">
            <table className="w-full" data-id="element-491">
              <thead data-id="element-492">
                <tr className="bg-gray-50" data-id="element-493">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-494">
                    Task
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-495">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-496">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-497">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-498">
                {committee.tasks.map(task => <tr key={task.id} data-id="element-499">
                    <td className="px-6 py-4" data-id="element-500">
                      <div className="font-medium text-gray-900" data-id="element-501">
                        {task.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-502">
                      <div className="flex items-center" data-id="element-503">
                        <Calendar size={16} className="text-gray-500 mr-2" data-id="element-504" />
                        <span data-id="element-505">{task.dueDate}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-506">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(task.status)}`} data-id="element-507">
                        {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-508">
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-509">
                        View
                      </button>
                      <button className="text-orange-500 hover:text-orange-700" data-id="element-510">
                        Edit
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Meetings Tab */}
      {activeTab === 'meetings' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-511">
          <div className="flex justify-between items-center mb-4" data-id="element-512">
            <h2 className="text-lg font-semibold" data-id="element-513">Meetings</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-514">
              Schedule Meeting
            </button>
          </div>
          <div className="space-y-4" data-id="element-515">
            {committee.meetings.map(meeting => <div key={meeting.id} className="border border-gray-200 rounded-lg p-4" data-id="element-516">
                <h3 className="font-medium mb-2" data-id="element-517">{meeting.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-518">
                  <div className="flex items-center text-sm text-gray-500" data-id="element-519">
                    <Calendar size={16} className="text-gray-500 mr-2" data-id="element-520" />
                    <span data-id="element-521">{meeting.date}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-522">
                    <Clock size={16} className="text-gray-500 mr-2" data-id="element-523" />
                    <span data-id="element-524">{meeting.time}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-525">
                    <Users size={16} className="text-gray-500 mr-2" data-id="element-526" />
                    <span data-id="element-527">{meeting.location}</span>
                  </div>
                </div>
                <div className="mt-3 flex space-x-2" data-id="element-528">
                  <button className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors" data-id="element-529">
                    View Details
                  </button>
                  <button className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors" data-id="element-530">
                    Edit
                  </button>
                </div>
              </div>)}
          </div>
        </div>}

      {/* Documents Tab */}
      {activeTab === 'documents' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-531">
          <div className="flex justify-between items-center mb-4" data-id="element-532">
            <h2 className="text-lg font-semibold" data-id="element-533">Documents</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-534">
              Upload Document
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-535">
            <table className="w-full" data-id="element-536">
              <thead data-id="element-537">
                <tr className="bg-gray-50" data-id="element-538">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-539">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-540">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-541">
                    Uploaded Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-542">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-543">
                {committee.documents.map(document => <tr key={document.id} data-id="element-544">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-545">
                      <div className="font-medium text-gray-900" data-id="element-546">
                        {document.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-547">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${document.type === 'PDF' ? 'bg-red-100 text-red-800' : document.type === 'DOCX' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-548">
                        {document.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-549">
                      {document.uploadedDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-550">
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-551">
                        View
                      </button>
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-552">
                        Download
                      </button>
                      <button className="text-red-500 hover:text-red-700" data-id="element-553">
                        Delete
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Status History Tab */}
      {activeTab === 'history' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-554">
          <div className="flex justify-between items-center mb-4" data-id="element-555">
            <h2 className="text-lg font-semibold" data-id="element-556">Status History</h2>
          </div>
          <div className="relative" data-id="element-557">
            {/* Timeline */}
            <div className="border-l-2 border-gray-200 absolute h-full left-6 top-0" data-id="element-558"></div>
            {/* Timeline Items */}
            <div className="space-y-6 relative" data-id="element-559">
              {committee.statusHistory.map((item, index) => <div key={item.id} className="ml-12 relative pb-4" data-id="element-560">
                  {/* Timeline Dot */}
                  <div className="absolute -left-12 mt-1" data-id="element-561">
                    <div className="bg-orange-500 h-6 w-6 rounded-full flex items-center justify-center" data-id="element-562">
                      <span className="text-white text-xs" data-id="element-563">
                        {committee.statusHistory.length - index}
                      </span>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-564">
                    <div className="flex justify-between mb-2" data-id="element-565">
                      <span className="font-semibold text-gray-900" data-id="element-566">
                        {item.status}
                      </span>
                      <span className="text-sm text-gray-500" data-id="element-567">{item.date}</span>
                    </div>
                    <p className="text-gray-700 mb-2" data-id="element-568">{item.note}</p>
                    <div className="text-sm text-gray-500" data-id="element-569">
                      Updated by: {item.by}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Formation Decision Document */}
          <div className="mt-8 border-t border-gray-200 pt-6" data-id="element-570">
            <h3 className="font-medium mb-4" data-id="element-571">Formation Decision</h3>
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-572">
              <div className="flex items-start" data-id="element-573">
                <div className="bg-blue-100 p-2 rounded" data-id="element-574">
                  <FileText className="text-blue-500" size={24} data-id="element-575" />
                </div>
                <div className="ml-3" data-id="element-576">
                  <h4 className="font-medium" data-id="element-577">
                    Decision #
                    {committeeId === 1 ? 'DEC-2023-001' : 'DEC-2023-002'}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-578">
                    Official formation decision document for {committee.name}
                  </p>
                  <div className="mt-2" data-id="element-579">
                    <button className="px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors" data-id="element-580">
                      View Document
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Cancel Committee Modal */}
      {showCancelModal && <CancelCommitteeModal committeeName={committee.name} onCancel={() => setShowCancelModal(false)} onConfirm={handleCancelCommittee} data-id="element-581" />}
    </div>;
}